/**
 * BeteiligtenTab — Beteiligte einer Akte, gruppiert nach Rolle.
 * Liest aus akten_beteiligte (Supabase), erlaubt manuelles Anlegen und Löschen.
 * Der Kontakt-Picker (aus CRM) ist Stub bis Sub-Issue #499 implementiert ist.
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { LCS_BETRIEB_NAME } from '@shared/const';
import {
  Users, Plus, Trash2, Loader2, Info, Phone, Mail, UserPlus, X, Save, Wrench, Key,
} from 'lucide-react';

// ─── Typen ────────────────────────────────────────────────────────────────────

type Akte = Record<string, any>;

interface BeteiligterRow {
  id: string;
  akte_id: string;
  kontakt_id: string | null;
  rolle: string;
  name: string | null;
  email: string | null;
  telefon: string | null;
  notizen: string | null;
  created_at: string;
  betrieb_id: string | null;
  betriebe: { firma: string } | null;
}

interface AddForm {
  rolle: string;
  name: string;
  email: string;
  telefon: string;
  notizen: string;
}

// ─── Rollen-Definitionen ──────────────────────────────────────────────────────

export const BETEILIGTE_ROLLEN: Array<{
  code: string;
  label: string;
  bg: string;
  text: string;
}> = [
  { code: 'anspruchsteller',    label: 'Anspruchsteller',       bg: 'bg-emerald-100', text: 'text-emerald-800' },
  { code: 'fahrer',             label: 'Fahrer',                bg: 'bg-sky-100',     text: 'text-sky-800' },
  { code: 'halter',             label: 'Halter',                bg: 'bg-cyan-100',    text: 'text-cyan-800' },
  { code: 'gegner',             label: 'Unfallgegner',          bg: 'bg-red-100',     text: 'text-red-800' },
  { code: 'versicherung_gegner',label: 'Versicherung Gegner',   bg: 'bg-orange-100',  text: 'text-orange-800' },
  { code: 'eigene_versicherung', label: 'Eigene Versicherung',  bg: 'bg-blue-100',    text: 'text-blue-800' },
  { code: 'anwalt',             label: 'Anwalt',                bg: 'bg-indigo-100',  text: 'text-indigo-800' },
  { code: 'werkstatt',          label: 'Werkstatt',             bg: 'bg-amber-100',   text: 'text-amber-800' },
  { code: 'mietwagen',          label: 'Mietwagen',             bg: 'bg-violet-100',  text: 'text-violet-800' },
  { code: 'sonstige',           label: 'Sonstige',              bg: 'bg-gray-100',    text: 'text-gray-700' },
];

const EMPTY_ADD_FORM: AddForm = { rolle: '', name: '', email: '', telefon: '', notizen: '' };

// ─── Kleine Primitiven ────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      {children}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
      <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <p className="text-[12px] text-gray-500">{text}</p>
    </div>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────

export default function BeteiligtenTab({ akte, onChanged }: { akte: Akte; onChanged?: () => void }) {
  const [beteiligte, setBeteiligte] = useState<BeteiligterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(EMPTY_ADD_FORM);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('akten_beteiligte')
      .select('id, akte_id, kontakt_id, rolle, name, email, telefon, notizen, created_at, betrieb_id, betriebe(firma)')
      .eq('akte_id', akte.id)
      .order('created_at', { ascending: true });
    if (error) {
      toast.error('Fehler beim Laden der Beteiligten', { description: error.message });
    } else {
      setBeteiligte((data ?? []) as unknown as BeteiligterRow[]);
    }
    setLoading(false);
  }, [akte.id]);

  useEffect(() => { void laden(); }, [laden]);

  const hasWerkstatt = beteiligte.some((b) => b.rolle === 'werkstatt');
  const hasMietgo = beteiligte.some((b) => b.rolle === 'mietwagen');

  const handleAddMietgo = useCallback(async () => {
    if (adding) return;
    setAdding(true);
    try {
      const { data: betrieb, error: betriebErr } = await supabase
        .from('betriebe')
        .select('id, firma')
        .ilike('firma', '%LCS%')
        .maybeSingle();

      if (betriebErr) {
        toast.error('Datenbankfehler bei Betrieb-Suche', { description: betriebErr.message });
        return;
      }
      if (!betrieb) {
        toast.error('MietGo-Betrieb nicht gefunden');
        return;
      }

      const { data: existing, error: existingErr } = await supabase
        .from('akten_beteiligte')
        .select('id')
        .eq('akte_id', akte.id)
        .eq('rolle', 'mietwagen')
        .maybeSingle();

      if (existingErr) {
        toast.error('Fehler beim Prüfen', { description: existingErr.message });
        return;
      }

      if (!existing) {
        const { error: insertErr } = await supabase
          .from('akten_beteiligte')
          .insert({ akte_id: akte.id, rolle: 'mietwagen', betrieb_id: betrieb.id });
        if (insertErr) {
          toast.error('Fehler beim Hinzufügen', { description: insertErr.message });
          return;
        }
      }

      const { error: updateErr } = await supabase
        .from('akten')
        .update({ mietwagen_in_anspruch: true })
        .eq('id', akte.id);
      if (updateErr) {
        toast.error('Fehler beim Aktualisieren', { description: updateErr.message });
        return;
      }

      toast.success(existing ? 'MietGo ist bereits als Beteiligter eingetragen' : 'MietGo als Beteiligter hinzugefügt');
      void laden();
      onChanged?.();
    } finally {
      setAdding(false);
    }
  }, [akte.id, adding, laden, onChanged]);

  const handleAddLcsWerkstatt = useCallback(async () => {
    if (adding) return;
    setAdding(true);
    try {
      // Fetch LCS GmbH directly — typ was changed to 'holding' in migration
      // 20260425_betriebe_struktur_holding.sql; querying by typ='werkstatt'
      // would hit the deprecated 'Werkstatt Koop' row instead.
      const { data: betrieb, error: betriebErr } = await supabase
        .from('betriebe')
        .select('id, firma, strasse, plz, ort, telefon, email')
        .eq('name', LCS_BETRIEB_NAME)
        .eq('deprecated', false)
        .maybeSingle();

      if (betriebErr) {
        toast.error('Datenbankfehler bei Betrieb-Suche', { description: betriebErr.message });
        return;
      }
      if (!betrieb) {
        toast.error('LCS Werkstatt-Betrieb nicht gefunden');
        return;
      }

      const { data: existing, error: existingErr } = await supabase
        .from('akten_beteiligte')
        .select('id')
        .eq('akte_id', akte.id)
        .eq('rolle', 'werkstatt')
        .maybeSingle();

      if (existingErr) {
        toast.error('Fehler beim Prüfen', { description: existingErr.message });
        return;
      }

      if (!existing) {
        const { error: insertErr } = await supabase
          .from('akten_beteiligte')
          .insert({ akte_id: akte.id, rolle: 'werkstatt', betrieb_id: betrieb.id });
        if (insertErr) {
          toast.error('Fehler beim Hinzufügen', { description: insertErr.message });
          return;
        }
      }

      const updates: Record<string, unknown> = { werkstatt_in_anspruch: true };
      if (!akte.werkstatt_name) {
        updates.werkstatt_name = 'LCS Werkstatt';
        if (betrieb.strasse) updates.werkstatt_adresse = betrieb.strasse;
        if (betrieb.plz) updates.werkstatt_plz = betrieb.plz;
        if (betrieb.ort) updates.werkstatt_ort = betrieb.ort;
        if (betrieb.telefon) updates.werkstatt_telefon = betrieb.telefon;
        if (betrieb.email) updates.werkstatt_email = betrieb.email;
      }

      const { error: updateErr } = await supabase
        .from('akten')
        .update(updates)
        .eq('id', akte.id);
      if (updateErr) {
        toast.error('Fehler beim Aktualisieren', { description: updateErr.message });
        return;
      }

      toast.success(existing ? 'LCS Werkstatt ist bereits als Beteiligter eingetragen' : 'LCS Werkstatt als Beteiligter hinzugefügt');
      void laden();
      onChanged?.();
    } finally {
      setAdding(false);
    }
  }, [akte.id, akte.werkstatt_name, adding, laden, onChanged]);

  const loeschen = async (id: string) => {
    setDeletingId(id);
    const beteiligter = beteiligte.find((b) => b.id === id);
    const { error } = await supabase.from('akten_beteiligte').delete().eq('id', id);
    if (error) {
      toast.error('Fehler beim Löschen', { description: error.message });
    } else {
      toast.success('Beteiligter gelöscht');
      setBeteiligte((prev) => prev.filter((b) => b.id !== id));
      if (beteiligter?.rolle === 'werkstatt') {
        const remaining = beteiligte.filter((b) => b.id !== id && b.rolle === 'werkstatt');
        if (remaining.length === 0) {
          const { error: updateErr } = await supabase.from('akten').update({ werkstatt_in_anspruch: false }).eq('id', akte.id);
          if (updateErr) toast.error('Fehler beim Zurücksetzen', { description: updateErr.message });
        }
      }
      onChanged?.();
    }
    setDeletingId(null);
  };

  const neuErfassenSpeichern = async () => {
    if (!addForm.rolle) { toast.error('Bitte eine Rolle auswählen'); return; }
    if (!addForm.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    const { error } = await supabase.from('akten_beteiligte').insert({
      akte_id: akte.id,
      rolle: addForm.rolle,
      name: addForm.name.trim() || null,
      email: addForm.email.trim() || null,
      telefon: addForm.telefon.trim() || null,
      notizen: addForm.notizen.trim() || null,
    });
    if (error) {
      toast.error('Fehler beim Speichern', { description: error.message });
    } else {
      toast.success('Beteiligter angelegt');
      setShowForm(false);
      setAddForm(EMPTY_ADD_FORM);
      void laden();
      onChanged?.();
    }
    setSaving(false);
  };

  const byRolle = (code: string) => beteiligte.filter((b) => b.rolle === code);

  return (
    <div className="space-y-4">
      {/* Quick-Actions: Betriebe als Beteiligte hinzufügen */}
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="lcs-werkstatt-hinzufuegen-button"
          onClick={handleAddLcsWerkstatt}
          disabled={adding || hasWerkstatt}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors
            ${hasWerkstatt
              ? 'bg-red-50 border border-red-200 text-red-700 cursor-default'
              : 'bg-red-600 hover:bg-red-700 text-white border border-transparent'
            }`}
        >
          {adding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : hasWerkstatt ? (
            <Wrench className="w-3.5 h-3.5" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          {hasWerkstatt ? 'LCS Werkstatt (Beteiligter)' : 'LCS Werkstatt'}
        </button>
        <button
          data-testid="mietgo-hinzufuegen-button"
          onClick={handleAddMietgo}
          disabled={adding || hasMietgo}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors
            ${hasMietgo
              ? 'bg-amber-50 border border-amber-200 text-amber-700 cursor-default'
              : 'bg-amber-500 hover:bg-amber-600 text-white border border-transparent'
            }`}
        >
          {adding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : hasMietgo ? (
            <Key className="w-3.5 h-3.5" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          {hasMietgo ? 'MietGo (Mietwagen)' : 'Mietgo hinzufügen'}
        </button>
      </div>

      {/* Kopfzeile */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-gray-900">Beteiligte</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {loading ? 'Lade…' : `${beteiligte.length} Beteiligte in dieser Akte`}
            </p>
          </div>
          <button
            onClick={() => { setShowForm(true); setAddForm(EMPTY_ADD_FORM); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> neu erfassen
          </button>
        </div>

        {/* Formular: neu erfassen */}
        {showForm && (
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-semibold text-blue-800">Neuen Beteiligten erfassen</p>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1">Rolle *</label>
                <select
                  value={addForm.rolle}
                  onChange={(e) => setAddForm((f) => ({ ...f, rolle: e.target.value }))}
                  className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="">— Rolle wählen —</option>
                  {BETEILIGTE_ROLLEN.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1">Name *</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Vor- und Nachname / Firmenname"
                  className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1">E-Mail</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="beispiel@domain.de"
                  className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1">Telefon</label>
                <input
                  type="tel"
                  value={addForm.telefon}
                  onChange={(e) => setAddForm((f) => ({ ...f, telefon: e.target.value }))}
                  placeholder="+49 …"
                  className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[12px] text-gray-500 block mb-1">Notizen</label>
                <input
                  type="text"
                  value={addForm.notizen}
                  onChange={(e) => setAddForm((f) => ({ ...f, notizen: e.target.value }))}
                  placeholder="Optionale Notiz"
                  className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowForm(false)}
                className="px-3 py-1.5 text-[12px] text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={neuErfassenSpeichern}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Speichern
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 py-4 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade Beteiligte…
          </div>
        )}
      </Card>

      {/* Rollen-Gruppen */}
      {!loading && BETEILIGTE_ROLLEN.map((rolle) => {
        const eintraege = byRolle(rolle.code);
        return (
          <Card key={rolle.code}>
            {/* Rollen-Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded ${rolle.bg} ${rolle.text}`}>
                  {rolle.label}
                </span>
                {eintraege.length > 0 && (
                  <span className="text-[11px] text-gray-400 font-medium">{eintraege.length}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {/* Stub: aus Kontakten */}
                <button
                  disabled
                  title="Kontakt-Picker folgt in Sub-Issue #499"
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-gray-400 bg-gray-50 border border-gray-200 rounded-lg cursor-not-allowed opacity-60"
                >
                  <UserPlus className="w-3.5 h-3.5" /> aus Kontakten
                </button>
                {/* neu erfassen (setzt Rolle vor) */}
                <button
                  onClick={() => {
                    setAddForm({ ...EMPTY_ADD_FORM, rolle: rolle.code });
                    setShowForm(true);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> neu erfassen
                </button>
              </div>
            </div>

            {/* Einträge oder Empty-State */}
            {eintraege.length === 0 ? (
              <EmptyState text={`Noch kein ${rolle.label} erfasst`} />
            ) : (
              <div className="space-y-2">
                {eintraege.map((b) => (
                  <div key={b.id} className="flex items-start justify-between gap-3 py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-gray-900 truncate">
                        {b.name ?? b.betriebe?.firma ?? '(kein Name)'}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                        {b.telefon && (
                          <a href={`tel:${b.telefon}`} className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-800">
                            <Phone className="w-3 h-3" /> {b.telefon}
                          </a>
                        )}
                        {b.email && (
                          <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-800 truncate max-w-[220px]">
                            <Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{b.email}</span>
                          </a>
                        )}
                        {b.notizen && (
                          <span className="text-[11px] text-gray-400 italic truncate max-w-[200px]">{b.notizen}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => loeschen(b.id)}
                      disabled={deletingId === b.id}
                      title="Beteiligter löschen"
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                    >
                      {deletingId === b.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
